import { useCallback, useState } from 'react'
import LiabilityStatementDialog from './LiabilityStatementDialog'
import UnsubscribedNotice from './UnsubscribedNotice'
import { messages } from '../Message'

// Wraps the onFlagChange a details section is given, so the marketing flag cannot be
// applied without the statement having been answered first. The wrapper keeps the frozen
// three-argument shape and adds a fourth argument carrying the answer; a section only
// ever calls with three, and the fourth travels from here to whatever applies the change.
export default function useMarketingConsent({ details = [], onFlagChange }) {
    const [pending, setPending] = useState(null)
    const [refused, setRefused] = useState(null)

    const request = useCallback(async (detailId, flagKey, wanted) => {
        if (flagKey !== 'marketing' || !wanted) {
            return onFlagChange?.(detailId, flagKey, wanted)
        }

        const detail = details.find((one) => one.id === detailId) ?? { id: detailId }

        if (detail.consent === 'unsubscribed') {
            setRefused(detail)
            throw new Error(messages.CONSENT_UNSUBSCRIBED)
        }

        return new Promise((resolve, reject) => {
            setPending({ detail, resolve, reject })
        })
    }, [details, onFlagChange])

    const approve = useCallback(async (wordingVersion) => {
        const answered = pending

        if (answered == null) {
            return
        }

        setPending(null)

        try {
            await onFlagChange?.(answered.detail.id, 'marketing', true, {
                approved: true,
                wordingVersion,
            })
            answered.resolve()
        }
        catch (error) {
            answered.reject(error)
        }
    }, [pending, onFlagChange])

    // The decline still travels to the server, because a firm that was asked and said no
    // has to leave a record of having been asked. The refusal afterwards is this hook's
    // own: a handler that ignores the answer and resolves must not get the flag applied.
    const decline = useCallback(async (wordingVersion) => {
        const answered = pending

        if (answered == null) {
            return
        }

        setPending(null)

        let failure = null

        try {
            await onFlagChange?.(answered.detail.id, 'marketing', true, {
                approved: false,
                wordingVersion,
            })
        }
        catch (error) {
            failure = error
        }

        answered.reject(failure ?? new Error(messages.CONSENT_STATEMENT_DECLINED))
    }, [pending, onFlagChange])

    const dialog = (
        <>
            <LiabilityStatementDialog
                open={pending != null}
                detail={pending?.detail}
                onApprove={approve}
                onDecline={decline}
            />

            <UnsubscribedNotice
                open={refused != null}
                detail={refused}
                onClose={() => setRefused(null)}
            />
        </>
    )

    return {
        onFlagChange: request,
        dialog,
    }
}
